import { useEffect, useState, useRef } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PricingTwoCol } from "@/pages/Landing";
import { Minus, Plus } from "lucide-react";

const PRICE = {
  monthly: { base: 5, addon: 3, interval: "mo", label: "month" },
  yearly: { base: 45, addon: 27, interval: "school year", label: "year" },
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
};

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [seats, setSeats] = useState(1);
  const [startNow, setStartNow] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const autoCheckoutFired = useRef(false);

  useEffect(() => {
    if (!user) { setTrialInfo(null); return; }
    let cancelled = false;
    api.get("/payments/trial-info").then((r) => {
      if (!cancelled) setTrialInfo(r.data);
    }).catch(() => { /* ignore — non-blocking */ });
    return () => { cancelled = true; };
  }, [user]);

  const cfg = PRICE[billing];
  const total = cfg.base + (seats - 1) * cfg.addon;
  const monthlyEquivalent = billing === "yearly" ? Math.round((total / 12) * 100) / 100 : total;
  const monthlyIfPaidMonthly = PRICE.monthly.base + (seats - 1) * PRICE.monthly.addon;
  const yearlySavings = billing === "yearly" ? Math.round((monthlyIfPaidMonthly * 12 - total) * 100) / 100 : 0;

  // Effective trial that will be applied at checkout
  const remainingTrial = trialInfo?.trial_days_remaining || 0;
  const inActiveTrial = trialInfo?.is_trial_active && remainingTrial > 0;
  const trialExpired = trialInfo?.trial_expired;
  const effectiveTrialDays = startNow ? 0 : (inActiveTrial ? remainingTrial : 0);
  const trialEndsLabel = formatDate(trialInfo?.trial_ends_at);

  const startCheckout = async () => {
    if (!user) {
      // Preserve the user's intent through the auth flow.
      const next = "/pricing?action=checkout";
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return;
    }
    setLoading(billing);
    try {
      const r = await api.post("/payments/checkout", {
        plan: billing,
        seats,
        origin_url: window.location.origin,
        start_now: startNow,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  // Auto-fire checkout when arriving at /pricing?action=checkout
  // (this is how the Summit Pass card on /landing routes logged-in users
  // straight to Stripe, and how logged-out users return here after login).
  useEffect(() => {
    if (autoCheckoutFired.current) return;
    if (searchParams.get("action") !== "checkout") return;
    if (!user) return;            // wait for AuthContext to resolve
    if (trialInfo === null) return; // wait for trial info before computing price
    autoCheckoutFired.current = true;
    // Strip the param so a refresh doesn't re-fire
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    startCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, trialInfo, searchParams]);

  // CTA + footnote vary by state
  const cta = inActiveTrial && !startNow
    ? `Lock in my Summit Pass`
    : (startNow ? "Start premium today" : "Reach the summit");

  const billingFootnote = (() => {
    if (startNow) return `You'll be charged $${total.toFixed(2)} today. No trial.`;
    if (inActiveTrial) {
      return trialEndsLabel
        ? `No charge today. You'll be billed $${total.toFixed(2)} on ${trialEndsLabel} when your trial ends.`
        : `No charge today. You'll be billed $${total.toFixed(2)} when your remaining ${remainingTrial}-day trial ends.`;
    }
    if (trialExpired) return `You'll be charged $${total.toFixed(2)} today.`;
    return `First ${remainingTrial || 14} days free • Auto-renews`;
  })();

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-16">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Expedition outfitter</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black text-mm-navy mt-2">Choose your trail</h1>
          <p className="text-mm-navy/70 mt-2 max-w-xl mx-auto">
            {inActiveTrial
              ? `You're on day ${14 - remainingTrial + 1} of your 14-day trial. Lock in your Summit Pass anytime — your remaining trial days are preserved.`
              : (trialExpired
                ? "Your free trial has ended. Reactivate Summit Pass anytime — your progress is saved."
                : "14-day free trial — no charge until day 15. Auto-renews; cancel anytime from your account.")}
          </p>
        </div>

        {/* Marketing pricing (matches landing) */}
        <div className="mt-12"><PricingTwoCol /></div>

        {/* School / district PO callout */}
        <div className="mt-8 text-center text-sm text-mm-navy/75" data-testid="pricing-po-callout">
          Are you a school or district administrator? We accept purchase orders.{" "}
          <Link to="/schools/purchase-order" className="font-bold text-mm-navy underline hover:text-mm-steel">Learn more →</Link>
        </div>

        {/* Live checkout for signed-in users */}
        <div className="mt-16 max-w-4xl mx-auto bg-white border border-mm-navy/10 rounded-2xl p-8" data-testid="checkout-card">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Build your team</div>
          <h2 className="font-heading text-2xl font-black text-mm-navy mt-2">Pick climbers + billing cadence</h2>

          {/* Trial banner for in-trial users */}
          {inActiveTrial && (
            <div className="mt-5 rounded-xl bg-mm-parchment border-2 border-mm-gold/40 p-4 flex items-start gap-3" data-testid="trial-banner">
              <div className="text-xl">🏕️</div>
              <div className="text-sm text-mm-navy/85 leading-snug">
                <div className="font-heading font-black">
                  {remainingTrial} day{remainingTrial === 1 ? "" : "s"} left on your free trial
                </div>
                <div className="mt-1">
                  Subscribe now to lock in your seat — we'll preserve your remaining trial and bill you
                  {trialEndsLabel ? ` on ${trialEndsLabel}` : ` after day 14`}.
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 inline-flex rounded-full bg-mm-navy/5 p-1">
            <button onClick={() => setBilling("monthly")} className={`px-5 py-2 rounded-full font-heading font-bold text-sm ${billing === "monthly" ? "bg-mm-gold text-mm-navy" : "text-mm-navy"}`} data-testid="billing-monthly">Monthly</button>
            <button onClick={() => setBilling("yearly")} className={`px-5 py-2 rounded-full font-heading font-bold text-sm ${billing === "yearly" ? "bg-mm-gold text-mm-navy" : "text-mm-navy"}`} data-testid="billing-yearly">Yearly — save $15+</button>
          </div>

          {/* Seat selector */}
          <div className="mt-6 rounded-2xl bg-mm-parchment border-2 border-mm-navy/10 p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Climbers on your account</div>
                <div className="text-mm-navy/70 text-sm mt-1">First climber: ${cfg.base}/{cfg.label} • each additional: ${cfg.addon}/{cfg.label}</div>
              </div>
              <div className="flex items-center gap-3" data-testid="seat-selector">
                <button onClick={() => setSeats(Math.max(1, seats - 1))} disabled={seats <= 1}
                  className="w-10 h-10 rounded-full border-2 border-mm-navy/20 text-mm-navy hover:border-mm-steel disabled:opacity-40 flex items-center justify-center"
                  data-testid="seat-decrement"><Minus size={16} strokeWidth={2.5} /></button>
                <div className="text-2xl font-heading font-black text-mm-navy w-12 text-center" data-testid="seat-count">{seats}</div>
                <button onClick={() => setSeats(Math.min(20, seats + 1))} disabled={seats >= 20}
                  className="w-10 h-10 rounded-full bg-mm-gold text-mm-navy hover:bg-mm-goldHover disabled:opacity-40 flex items-center justify-center"
                  data-testid="seat-increment"><Plus size={16} strokeWidth={2.5} /></button>
              </div>
            </div>
            <div className="mt-4 text-sm text-mm-navy/85" data-testid="seat-summary">
              <strong>{seats} climber{seats === 1 ? "" : "s"} — ${total}/{cfg.label}</strong>
              {billing === "yearly" && yearlySavings > 0 && (
                <span className="ml-2 text-mm-gold font-bold">(save ${yearlySavings.toFixed(2)} vs monthly)</span>
              )}
              {billing === "yearly" && seats > 1 && (
                <div className="text-xs text-mm-navy/60 mt-1" data-testid="yearly-comparison">
                  Add a climber for ${PRICE.yearly.addon}/year instead of ${PRICE.monthly.addon * 12}/year monthly.
                </div>
              )}
            </div>
          </div>

          {/* Start-now toggle for trial users */}
          {inActiveTrial && (
            <label
              htmlFor="start-now-toggle"
              className="mt-5 flex items-start gap-3 rounded-xl border-2 border-mm-navy/10 p-4 cursor-pointer hover:border-mm-steel/40"
              data-testid="start-now-toggle-row"
            >
              <input
                id="start-now-toggle"
                type="checkbox"
                checked={startNow}
                onChange={(e) => setStartNow(e.target.checked)}
                className="mt-1 h-4 w-4 accent-mm-gold"
                data-testid="start-now-toggle"
              />
              <div className="flex-1">
                <div className="font-heading font-bold text-mm-navy">Start premium today</div>
                <div className="text-sm text-mm-navy/70 mt-1">
                  Skip the rest of your free trial and get billed <strong>${total.toFixed(2)}</strong> immediately.
                  Useful if you want to add more than {trialInfo?.plan === "trial" ? "5" : "1"} climbers or activate paid-only features right now.
                </div>
              </div>
            </label>
          )}

          <div className="mt-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="font-heading text-4xl font-black text-mm-navy" data-testid="checkout-amount">
                ${total.toFixed(2)}
                <span className="text-base font-bold text-mm-navy/60 ml-1">/ {cfg.label}</span>
              </div>
              <div className="text-sm text-mm-navy/60 mt-1">≈ ${monthlyEquivalent.toFixed(2)}/mo • Premium for {seats} climber{seats === 1 ? "" : "s"}</div>
              <div className="text-xs text-mm-steel font-bold mt-1 uppercase tracking-widest" data-testid="checkout-footnote">{billingFootnote}</div>
            </div>
            <button
              onClick={startCheckout}
              disabled={loading !== null}
              className="btn-trail btn-gold"
              data-testid="checkout-btn"
            >
              {loading ? "Heading to checkout..." : cta}
            </button>
          </div>
          {!user && (
            <p className="text-xs text-mm-navy/60 mt-4">You'll be asked to sign in to complete checkout.</p>
          )}
          {/* Debug-friendly state hint for QA */}
          {user && trialInfo && (
            <p className="text-[10px] text-mm-navy/40 mt-3" data-testid="trial-state-hint">
              State: plan={trialInfo.plan} · trial_days_remaining={remainingTrial} · effective_stripe_trial_days={effectiveTrialDays}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pricing;
